/**
 * The axios singleton, its auth-token state and its interceptors.
 *
 * This lives in a LEAF module on purpose. It used to sit at the top of
 * `services/api.ts`, which also does `export * from './api/index'`, while
 * `api/client.ts` did `import api from '../api'` — a cycle
 * (api.ts -> api/index.ts -> client.ts -> api.ts). Rollup has to evaluate
 * client.ts before api.ts's body, so client.ts's `export { api }` read the
 * `const api` while it was still in the temporal dead zone. The production
 * bundle threw "Cannot access 'X' before initialization" at load and the
 * portal rendered a white screen. tsc, eslint, vitest and `vite build` all
 * passed — none of them execute the bundle.
 *
 * Nothing here may import from `../api` or `./index`.
 */
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

let authToken: string | null = localStorage.getItem('fleet_jwt');

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem('fleet_jwt', token);
  } else {
    localStorage.removeItem('fleet_jwt');
  }
}

export function getAuthToken(): string | null {
  return authToken || localStorage.getItem('fleet_jwt');
}

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const impersonatedAccountId = localStorage.getItem('fleet_impersonated_account_id');
  if (impersonatedAccountId) {
    config.headers['X-Account-Id'] = impersonatedAccountId;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      setAuthToken(null);
      localStorage.removeItem('fleet_role');

      // The auth store persists `isAuthenticated` separately, under
      // `fleet-auth` (zustand persist). Dropping only the JWT left the store
      // still reporting a live session, which produced a full-page reload
      // loop that flashed the UI without ever rendering it:
      //
      //   401 -> here -> /login -> App.tsx sees isAuthenticated -> <Navigate to="/">
      //   -> RoleLanding fires getOnboardingStatus() -> 401 -> here -> ...
      //
      // Measured at 123 top-level navigations in 12s against QA. Clearing the
      // persisted key is what actually matters, because the redirect below
      // reloads the document and zustand rehydrates from localStorage; the
      // in-memory clearAuth() covers the case where we do not redirect.
      localStorage.removeItem('fleet-auth');
      void import('../../stores/authStore')
        .then(({ useAuthStore }) => useAuthStore.getState().clearAuth())
        .catch(() => { /* store not loaded yet; the reload clears it anyway */ });

      // Never bounce a page that is already the login page — a 401 from
      // anything rendered there would otherwise reload it forever.
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export { api };
export default api;
